import { siteConfig } from '../../shared/config/site';

export type NavigationSection = { id: string; label: string };

export type HireChapter = {
  id: string;
  label: string;
  title: string;
  body: string;
};

export type FleetItem = {
  id: string;
  label: string;
  summary: string;
  image: string;
};

export type ProcessBeat = {
  id: string;
  step: string;
  title: string;
  body: string;
};

export type ReviewQuote = {
  author: string;
  quote: string;
  rating: number;
};

export const navigationSections: NavigationSection[] = [
  { id: 'hire', label: 'Hire' },
  { id: 'fleet', label: 'Fleet' },
  { id: 'process', label: 'Process' },
  { id: 'yard', label: 'Yard' },
  { id: 'contact', label: 'Contact' },
];

export const hero = {
  brandLine: 'Inshore',
  brandSub: 'Scaffolding & Plant Hire',
  headline: 'Height, plant, and tools from Barratt Road.',
  supportSentence:
    'Scaffolding rental and plant hire for West Rand sites. Call the Factoria yard and get moving.',
  primaryCta: 'Call the yard',
  secondaryCta: 'See the fleet',
} as const;

export const hireChapters: HireChapter[] = [
  {
    id: 'scaffold',
    label: '01 · Scaffolding',
    title: 'Tube and board when the job needs height.',
    body: 'Scaffolding rental for renovations, industrial access, and short-run builds across Krugersdorp and the West Rand.',
  },
  {
    id: 'plant',
    label: '02 · Plant hire',
    title: 'Machines that move earth and material.',
    body: 'Plant and machinery hire for contractors who need digging, lifting, and site power without buying the fleet.',
  },
  {
    id: 'tools',
    label: '03 · Site tools',
    title: 'Drills, cleaners, and the gear that finishes the day.',
    body: 'Specialised hire gear sits beside the scaffold stock — including items locals struggle to find elsewhere on the West Rand.',
  },
];

export const fleetItems: FleetItem[] = [
  {
    id: 'scaffold',
    label: 'Scaffolding',
    summary: 'Access towers and tube stock for height work on residential and industrial sites.',
    image: '/images/ladder.webp',
  },
  {
    id: 'excavator',
    label: 'Excavator',
    summary: 'Plant for trenches, pads, and clear-outs when the programme needs digging capacity.',
    image: '/images/excavator-960.webp',
  },
  {
    id: 'generator',
    label: 'Generator',
    summary: 'Site power when the temporary supply has to keep tools and lights running.',
    image: '/images/generator-960.webp',
  },
  {
    id: 'drill',
    label: 'Power tools',
    summary: 'Rotary hammers and site drills for the finish work between scaffold lifts.',
    image: '/images/drill.webp',
  },
  {
    id: 'crane',
    label: 'Lifting',
    summary: 'Lifting support for loads that hand labour cannot move safely.',
    image: '/images/crane.webp',
  },
];

export const processBeats: ProcessBeat[] = [
  {
    id: 'call',
    step: '01',
    title: 'Call the yard',
    body: 'Tell us the job, the duration, and the gear you need. Same number that sits on Google Maps.',
  },
  {
    id: 'confirm',
    step: '02',
    title: 'Confirm availability',
    body: 'We check stock for scaffolding, plant, and tools, then hold it against your name for collection.',
  },
  {
    id: 'collect',
    step: '03',
    title: 'Collect on Barratt Road',
    body: 'Factoria yard at 2 Barratt Rd. Bring your paperwork, load out, and get the site moving.',
  },
];

export const reviewQuotes: ReviewQuote[] = [
  {
    author: 'Richard Starke',
    quote: 'Great Service and Good Price',
    rating: 5,
  },
  {
    author: 'Willie Dunckley',
    quote: 'Very good service and unbeatable prices.',
    rating: 5,
  },
  {
    author: 'Dirk Beukes',
    quote: 'Cheapest in the West Rand and one of few who has drain cleaning machines',
    rating: 5,
  },
];

export const contactBlurb = `Call ${siteConfig.phoneDisplay} for scaffolding and plant hire from Factoria. ${siteConfig.hoursNote}`;
